import React from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack,
  Alert,
  Paper,
  Button,
} from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useWorkspaceNavigation from "../../hooks/useWorkspaceNavigation";
import { NotificationResponseDto } from "../../shared/dtos/notification/NotificationResponseDto";

interface ViewNotificationModalProps {
  open: boolean;
  onClose: () => void;
  notification: NotificationResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewNotificationModal: React.FC<ViewNotificationModalProps> = ({
  open,
  onClose,
  notification,
  loading,
  error,
}) => {
  const { navigateToTab } = useWorkspaceNavigation();

  const handleNavigateToRelated = (): void => {
    if (
      !notification ||
      !notification.relatedEntityId ||
      !notification.relatedContext
    )
      return;

    let path = "";
    switch (notification.relatedContext) {
      case "PURCHASE_ORDER":
        path = `/purchase-orders/view/${notification.relatedEntityId}`;
        break;
      case "INVOICE":
        path = `/invoices/view/${notification.relatedEntityId}`;
        break;
      case "CREDIT_NOTE":
        path = `/credit-notes/view/${notification.relatedEntityId}`;
        break;
      case "DEBT":
        path = `/debts/view/${notification.relatedEntityId}`;
        break;
      default:
        return;
    }
    onClose();
    navigateToTab(path);
  };

  const renderContent = (): React.ReactNode => {
    if (loading) return <AppCircularProgress />;

    if (error)
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    if (!notification) return null;

    const isFailed = notification.status === "FAILED";

    return (
      <Box>
        {/* HEADER AREA */}
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ maxWidth: "75%" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {notification.subject}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              ID: {notification.id}
            </Typography>
          </Box>
          <Chip
            label={notification.status}
            color={
              notification.status === "SENT"
                ? "success"
                : isFailed
                  ? "error"
                  : "default"
            }
            variant="outlined"
            sx={{ fontWeight: "bold" }}
          />
        </Stack>

        {/* MESSAGE BODY */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "rgba(255,255,255,0.02)",
            borderRadius: 2,
            borderStyle: "dashed",
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {notification.messageBody}
          </Typography>
        </Paper>

        {/* METADATA GRID */}
        <Grid container spacing={2}>
          <DetailItem label="Recipient" value={notification.targetUserName} />
          <DetailItem
            label="Channel"
            value={notification.channel.replace(/_/g, " ")}
          />
          <DetailItem
            label="Dispatched By"
            value={notification.sentByUserName || "System"}
          />
          <DetailItem
            label="Created At"
            value={new Date(notification.createdAt).toLocaleString()}
          />
          <DetailItem
            label="Type / Category"
            value={notification.type.replace(/_/g, " ")}
          />

          {notification.isRead && notification.readAt && (
            <DetailItem
              label="Read Confirmation"
              value={`Read on ${new Date(notification.readAt).toLocaleString()}`}
            />
          )}
        </Grid>

        {/* RELATED ENTITY SECTION */}
        {notification.relatedEntityId && notification.relatedContext && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Stack
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  Related Context
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {notification.relatedContext.replace(/_/g, " ")}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<OpenInNewIcon />}
                onClick={handleNavigateToRelated}
              >
                View Linked Record
              </Button>
            </Stack>
          </Box>
        )}

        {/* FAILURE REASON */}
        {notification.failureReason && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Dispatch Failure Reason:
            </Typography>
            <Typography variant="body2">
              {notification.failureReason}
            </Typography>
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Notification Audit & Tracking"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewNotificationModal;
